import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
    Heart,
    Users,
    MapPin,
    Utensils,
    Sticker,
    Save,
    X,
    Sparkles,
    Info
} from 'lucide-react';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import LoadingSpinner from '../ui/LoadingSpinner';
import { createClientComponentClient } from '@/integrations/supabase/client';

interface ResidentMemoryFormProps {
    residentId: string;
    residentName: string;
    onClose: () => void;
    onSaved: () => void;
}

interface MemoryData {
    family: string;
    hometown: string;
    hobbies: string;
    favorite_foods: string;
    interests: string;
    important_details: string;
}

export const ResidentMemoryForm: React.FC<ResidentMemoryFormProps> = ({
    residentId,
    residentName,
    onClose,
    onSaved
}) => {
    const supabase = createClientComponentClient();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [data, setData] = useState<MemoryData>({
        family: '',
        hometown: '',
        hobbies: '',
        favorite_foods: '',
        interests: '',
        important_details: ''
    });

    useEffect(() => {
        const fetchMemory = async () => {
            try {
                const { data: memData, error } = await supabase
                    .from('user_memories')
                    .select('key_facts')
                    .eq('user_id', residentId)
                    .eq('category', 'global')
                    .maybeSingle();

                if (memData?.key_facts) {
                    const facts = memData.key_facts as Record<string, string>;
                    setData({
                        family: facts.family || '',
                        hometown: facts.hometown || '',
                        hobbies: facts.hobbies || '',
                        favorite_foods: facts.favorite_foods || '',
                        interests: facts.interests || '',
                        important_details: facts.important_details || ''
                    });
                }
            } catch (err) {
                console.error('Error fetching resident memory:', err);
            } finally {
                setLoading(false);
            }
        };

        fetchMemory();
    }, [residentId, supabase]);

    const handleSave = async () => {
        setSaving(true);
        try {
            const { error } = await supabase
                .from('user_memories')
                .upsert({
                    user_id: residentId,
                    category: 'global',
                    key_facts: {
                        ...data,
                        last_updated_by: 'facility_admin',
                        updated_at: new Date().toISOString()
                    },
                    updated_at: new Date().toISOString()
                }, { onConflict: 'user_id, category' });

            if (error) throw error;
            onSaved();
            onClose();
        } catch (err) {
            console.error('Error saving resident memory:', err);
            alert('Failed to save personalization details.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-slate-900/40 backdrop-blur-md z-[100] flex items-center justify-center p-4 sm:p-6"
            onClick={onClose}
        >
            <motion.div
                initial={{ scale: 0.9, y: 20, opacity: 0 }}
                animate={{ scale: 1, y: 0, opacity: 1 }}
                exit={{ scale: 0.9, y: 20, opacity: 0 }}
                transition={{ type: 'spring', damping: 25, stiffness: 300 }}
                className="glass-card w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col"
                onClick={e => e.stopPropagation()}
            >
                {/* Header */}
                <div className="p-8 border-b border-white/20 flex justify-between items-center bg-white/30">
                    <div className="flex items-center gap-4">
                        <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-emerald-400 to-teal-500 flex items-center justify-center shadow-lg shadow-emerald-200/50">
                            <Sparkles className="w-7 h-7 text-white" />
                        </div>
                        <div>
                            <h2 className="text-2xl font-bold text-white">Personalize Sage for {residentName}</h2>
                            <p className="text-slate-400 flex items-center gap-1.5 mt-0.5">
                                <Info className="w-4 h-4" /> This information helps Sage be more empathetic and tailored.
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="w-10 h-10 rounded-full bg-white/50 border border-white/50 flex items-center justify-center text-slate-400 hover:text-slate-400 hover:bg-white transition-all"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-8 custom-scrollbar space-y-8 bg-white/10">
                    {loading ? (
                        <div className="h-60 flex flex-col items-center justify-center gap-4">
                            <LoadingSpinner size="lg" />
                            <p className="text-slate-400 font-medium italic">Gathering memory files...</p>
                        </div>
                    ) : (
                        <>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div className="space-y-2">
                                    <label className="text-sm font-semibold text-white flex items-center gap-2 px-1">
                                        <Users className="w-4 h-4 text-emerald-500" /> Family & Loved Ones
                                    </label>
                                    <Input
                                        placeholder="e.g. Children (Sarah, Tom), Grandkids..."
                                        value={data.family}
                                        onChange={e => setData(prev => ({ ...prev, family: e.target.value }))}
                                        className="h-12 bg-white/50 border-white/50 focus:bg-white transition-all rounded-xl"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <label className="text-sm font-semibold text-white flex items-center gap-2 px-1">
                                        <MapPin className="w-4 h-4 text-emerald-500" /> Hometown / Roots
                                    </label>
                                    <Input
                                        placeholder="e.g. Born in Dublin, Grew up in Ohio"
                                        value={data.hometown}
                                        onChange={e => setData(prev => ({ ...prev, hometown: e.target.value }))}
                                        className="h-12 bg-white/50 border-white/50 focus:bg-white transition-all rounded-xl"
                                    />
                                </div>
                            </div>

                            <div className="space-y-2">
                                <label className="text-sm font-semibold text-white flex items-center gap-2 px-1">
                                    <Heart className="w-4 h-4 text-emerald-500" /> Hobbies & Pastimes
                                </label>
                                <Input
                                    placeholder="e.g. Chess, Gardening, Reading historical fiction..."
                                    value={data.hobbies}
                                    onChange={e => setData(prev => ({ ...prev, hobbies: e.target.value }))}
                                    className="h-12 bg-white/50 border-white/50 focus:bg-white transition-all rounded-xl"
                                />
                            </div>

                            <div className="space-y-2">
                                <label className="text-sm font-semibold text-white flex items-center gap-2 px-1">
                                    <Utensils className="w-4 h-4 text-emerald-500" /> Favorite Foods & Comforts
                                </label>
                                <Input
                                    placeholder="e.g. Apple pie, Earl Grey tea, Roast dinners..."
                                    value={data.favorite_foods}
                                    onChange={e => setData(prev => ({ ...prev, favorite_foods: e.target.value }))}
                                    className="h-12 bg-white/50 border-white/50 focus:bg-white transition-all rounded-xl"
                                />
                            </div>

                            <div className="space-y-2">
                                <label className="text-sm font-semibold text-white flex items-center gap-2 px-1">
                                    <Sticker className="w-4 h-4 text-emerald-500" /> Important Life Details
                                </label>
                                <textarea
                                    placeholder="Any other details Sage should know (e.g. Former teacher, Loves jazz...)"
                                    value={data.important_details}
                                    onChange={e => setData(prev => ({ ...prev, important_details: e.target.value }))}
                                    className="w-full min-h-[120px] p-4 bg-white/50 border border-white/50 focus:bg-white focus:ring-2 focus:ring-emerald-500/20 outline-none transition-all rounded-2xl resize-none text-white placeholder:text-slate-400"
                                />
                            </div>
                        </>
                    )}
                </div>

                {/* Footer */}
                <div className="p-8 bg-white/40 border-t border-white/20 flex justify-end gap-3">
                    <Button
                        variant="ghost"
                        onClick={onClose}
                        className="rounded-full px-8 h-12 text-slate-400 hover:text-white"
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSave}
                        disabled={saving || loading}
                        className="button-premium !h-12 !px-10 rounded-full flex items-center gap-2"
                    >
                        {saving ? <LoadingSpinner size="sm" /> : <Save className="w-4 h-4" />}
                        Save Personalization
                    </Button>
                </div>
            </motion.div>
        </motion.div>
    );
};
